import React, { useCallback, useMemo, useState } from "react"

import { FloatingPortal } from "@floating-ui/react"
import { flushSync } from "react-dom"
import { useTranslation } from "react-i18next"
import {
  atom,
  useRecoilCallback,
  useRecoilValue,
  useSetRecoilState,
} from "recoil"
import styled, { keyframes } from "styled-components"

import type { ImageryResponseDto } from "~/api/dto/imagery.dto"
import useFetchImageryOnDemand from "~/api/hooks/useFetchImageryOnDemand"
import { ConfirmModal } from "~/components/ConfirmModal"
import { isRetiredImagerySource } from "~/components/RetiredImagery/retired-imagery"
import {
  siteLocalState,
  siteRemoteState,
} from "~/containers/Proposal/state/site/site-state"
import type { Imagery, ImagerySource } from "~/graphql/client"

import { ReactComponent as BingIcon } from "@svg/phase/image-selection/bing.svg"
import { ReactComponent as GoogleIcon } from "@svg/phase/image-selection/google.svg"
import { ReactComponent as NearmapIcon } from "@svg/phase/image-selection/nearmap.svg"

import {
  findImageState,
  imageIndex,
  type IPlacedImage,
} from "../state/imageElements"
import { useResetAllPhases } from "../state/phase"
import { imageSourceState } from "../state/view"

const DEFAULT_SIDE = 1024

/**
 * Creates a key for the image based on source and creation date.
 * Must match the format used in mapImageToKey in imageElements.ts
 */
const makeImageKey = (img: ImageryResponseDto): string =>
  `${img.source} (${new Date(img.createdAt).toLocaleDateString(`en-us`, {
    dateStyle: `medium`,
  })})`

/**
 * Converts API response to the IPlacedImage format
 */
const makeImageState = (img: ImageryResponseDto): IPlacedImage => {
  const height = img.height ?? DEFAULT_SIDE
  const width = img.width ?? DEFAULT_SIDE

  return {
    filename: img.filename,
    imageDate: img.imageDate,
    source: img.source,
    x: (DEFAULT_SIDE - width) / 2,
    y: (DEFAULT_SIDE - height) / 2,
    w: width,
    h: height,
    isLocked: true,
    zoomLevel: img.zoomLevel ?? 20,
  }
}

/**
 * Preloads an image by creating an Image element and waiting for it to load.
 * This ensures the browser has the image cached before we try to display it.
 */
const preloadImage = (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = (): void => resolve()
    img.onerror = (): void => reject(new Error(`Failed to load image: ${url}`))
    img.src = url
  })
}

type ProviderName = `bing` | `google maps` | `nearmap`
type ApiProviderName = `nearmap`

const imageOptions: Record<
  ProviderName,
  { value: ProviderName; icon: React.ReactNode; apiName?: ApiProviderName }
> = {
  [`google maps`]: {
    value: `google maps`,
    icon: <GoogleIcon />,
  },
  // Retired: listed only while it is the Design's current image, never fetched or newly chosen.
  bing: {
    value: `bing`,
    icon: <BingIcon />,
  },
  nearmap: {
    value: `nearmap`,
    icon: <NearmapIcon />,
    apiName: `nearmap`,
  },
}

const supportedProviders: ProviderName[] = [`google maps`, `nearmap`]
const providersWithCurrentBing: ProviderName[] = [
  `google maps`,
  `bing`,
  `nearmap`,
]

export const ImageSelection: React.FC<{
  /**
   * Recovering a Design whose saved Bing image cannot load: list only supported sources and
   * keep the existing panel layout when the background is replaced.
   */
  isRecovery?: boolean
}> = ({ isRecovery = false }) => {
  const imageIds = useRecoilValue(imageIndex)
  const imageSource = useRecoilValue(imageSourceState)
  const setNewImageSource = useSetRecoilState(newImageSourceState)
  const site = useRecoilValue(siteRemoteState)
  const { mutate: fetchImagery, status } = useFetchImageryOnDemand()
  const isPending = status === `loading`
  const [fetchingProvider, setFetchingProvider] = useState<ProviderName | null>(
    null,
  )

  const setSiteRemote = useSetRecoilState(siteRemoteState)
  const setSiteLocal = useSetRecoilState(siteLocalState)

  // Add new imagery to the existing state (without replacing)
  // This updates both the image atoms AND the siteRemoteState so that
  // the imageSourceState selector can find the new imagery
  const addImageryToState = useCallback(
    (imagery: ImageryResponseDto[]) => {
      // Convert to GraphQL Imagery type for siteRemoteState
      const graphqlImagery: Imagery[] = imagery.map((img) => ({
        __typename: `Imagery` as const,
        id: img.id,
        createdAt: img.createdAt,
        updatedAt: img.updatedAt,
        filename: img.filename ?? ``,
        imageDate: img.imageDate,
        source: img.source as ImagerySource,
        height: img.height,
        width: img.width,
        zoomLevel: img.zoomLevel,
        positionedX: img.positionedX,
        positionedY: img.positionedY,
        positionedZoom: img.positionedZoom,
        design: null,
        site: null,
        siteId: site.id,
      }))

      // Update siteRemoteState to include the new imagery
      // This is needed because imageSourceState selector reads from safeSiteImageryState
      setSiteRemote((currentSite) => {
        // Type assertion needed because imagery can be Imagery | PoisonedImagery
        const newImagery = [
          ...currentSite.imagery,
          ...graphqlImagery,
        ] as typeof currentSite.imagery
        return {
          ...currentSite,
          imagery: newImagery,
        }
      })

      // Also update siteLocalState to keep them in sync
      setSiteLocal((currentSite) => {
        const newImagery = [
          ...currentSite.imagery,
          ...graphqlImagery,
        ] as typeof currentSite.imagery
        return {
          ...currentSite,
          imagery: newImagery,
        }
      })
    },
    [site.id, setSiteRemote, setSiteLocal],
  )

  // Update the image index atoms when site imagery changes
  const updateImageAtoms = useRecoilCallback(
    ({ set, snapshot }) =>
      async (imagery: ImageryResponseDto[]) => {
        const currentKeys = await snapshot.getPromise(imageIndex)
        const newKeys = [...currentKeys]

        for (const img of imagery) {
          const key = makeImageKey(img)
          const state = makeImageState(img)
          // Add to index if not already present
          if (!newKeys.includes(key)) {
            newKeys.push(key)
          }
          // Set the image state
          set(findImageState(key), state)
        }

        set(imageIndex, newKeys)
      },
    [],
  )

  // Map of provider name to their loaded image ID
  const loadedProviders = useMemo(() => {
    const map: Partial<Record<ProviderName, string>> = {}
    imageIds.forEach((id) => {
      const providerName = returnProviderName(id)
      map[providerName] = id
    })
    return map
  }, [imageIds])

  // Offer Bing only as the current selection of a Design that still shows its saved Bing image
  const isShowingBing =
    !isRecovery &&
    isRetiredImagerySource(imageSource[0]) &&
    loadedProviders.bing === imageSource[0]
  const providers = isShowingBing ? providersWithCurrentBing : supportedProviders

  const handleProviderClick = useCallback(
    (providerName: ProviderName) => {
      // If already selected, do nothing
      const loadedImageId = loadedProviders[providerName]
      if (loadedImageId && imageSource[0] === loadedImageId) return

      // Bing is retired: it can stay selected but is never newly chosen
      if (providerName === `bing`) return

      // If already loaded, just switch to it
      if (loadedImageId) {
        setNewImageSource(loadedImageId)
        return
      }

      // Need to fetch on-demand for non-Google providers
      const option = imageOptions[providerName]
      if (!option.apiName) return // Google is always pre-loaded

      setFetchingProvider(providerName)
      fetchImagery(
        { siteId: site.id, provider: option.apiName },
        {
          onSuccess: async (imagery) => {
            // Add the new imagery to Recoil state
            const validImagery = imagery.filter((img) => img.filename)
            if (validImagery.length > 0) {
              // Preload the actual image file before showing confirmation
              const imageUrl = validImagery[0].filename
              if (imageUrl) {
                try {
                  await preloadImage(imageUrl)
                } catch (e) {
                  // If preload fails, continue anyway - the image might still work
                  console.warn(`Failed to preload image:`, e)
                }
              }

              // Add to site state (for imageSourceState selector)
              // Use flushSync to ensure state updates are processed synchronously
              flushSync(() => {
                addImageryToState(validImagery)
              })

              // Add to image atoms (for the designer)
              await updateImageAtoms(validImagery)

              // Now that state is updated, set the new image source
              const newImageId = makeImageKey(validImagery[0])
              setNewImageSource(newImageId)
              setFetchingProvider(null)
            } else {
              setFetchingProvider(null)
            }
          },
          onError: () => {
            setFetchingProvider(null)
          },
        },
      )
    },
    [
      loadedProviders,
      imageSource,
      setNewImageSource,
      fetchImagery,
      site.id,
      addImageryToState,
      updateImageAtoms,
    ],
  )

  return (
    <>
      <Wrapper>
        {providers.map((providerName) => {
          const image = imageOptions[providerName]
          const loadedImageId = loadedProviders[providerName]
          const isSelected = loadedImageId && imageSource[0] === loadedImageId
          const isLoading = fetchingProvider === providerName && isPending
          const isNotLoaded =
            !loadedImageId &&
            providerName !== `google maps` &&
            providerName !== `bing`

          return (
            <li
              key={providerName}
              onClick={(e) => {
                e.preventDefault()
                if (isLoading) return
                handleProviderClick(providerName)
              }}
              data-loading={isLoading ? true : undefined}
              data-not-loaded={isNotLoaded ? true : undefined}
            >
              <input
                type="radio"
                id={providerName}
                name="imagery"
                checked={!!isSelected}
                readOnly
              />
              <label htmlFor={providerName}>
                <button data-role="select-button">
                  <div>
                    <span />
                  </div>
                </button>
                <div>{image.icon}</div>
                <p>
                  {image.value}
                  {isLoading && <LoadingSpinner />}
                  {isNotLoaded && !isLoading && (
                    <NotLoadedIndicator>Click to load</NotLoadedIndicator>
                  )}
                </p>
              </label>
            </li>
          )
        })}
      </Wrapper>
      <ConfirmImageSwitch preserveLayout={isRecovery} />
    </>
  )
}

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

const LoadingSpinner = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-left: 8px;
  border: 2px solid var(--botticelli-blue);
  border-top: 2px solid var(--blue);
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`

const NotLoadedIndicator = styled.span`
  display: inline-block;
  margin-left: 8px;
  font-size: 12px;
  color: var(--gray);
  font-style: italic;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0px 20px;
  li + li {
    border-top: 1px solid var(--botticelli-blue);
  }
  > li {
    display: flex;
    align-items: center;
    padding: 20px 0px;
    position: relative;
    cursor: pointer;
    &[data-loading="true"] {
      opacity: 0.7;
      cursor: wait;
    }
    &[data-not-loaded="true"] {
      opacity: 0.85;
    }
    input {
      position: absolute;
      left: -9999px;
      & + label > [data-role="select-button"] {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        width: 18px;
        height: 18px;
        border: 1px solid #ddd;
        border-radius: 100%;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        > div {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          background-color: var(--blue);
          border-radius: 100%;
          transition: all 0.2s ease;
          position: relative;
          span {
            display: block;
            flex-shrink: 0;
            position: relative;
            width: calc(100% - 10px);
            height: calc(100% - 10px);
            background-color: white;
            border-radius: 100%;
          }
        }
      }
      &:not(:checked) + label > [data-role="select-button"] > div {
        opacity: 0;
        transform: scale(0);
      }
      &:checked + label > [data-role="select-button"] > div {
        opacity: 1;
        transform: scale(1);
      }
    }
    label {
      display: flex;
      align-items: center;
      cursor: pointer;
      gap: 10px;
      text-transform: capitalize;
      > div {
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--botticelli-blue);
        border-radius: 4px;
        padding: 8px;
      }
      > p {
        font-size: 16px;
        font-weight: 500;
        color: var(--blue-dark-font);
        display: flex;
        align-items: center;
      }
    }
  }
`

const returnProviderName = (id: string): keyof typeof imageOptions => {
  const includesGoogle = id.includes(`google`)
  const includesBing = id.includes(`bing`)
  const includesNearmap = id.includes(`nearmap`)
  if (includesGoogle) return `google maps`
  if (includesBing) return `bing`
  if (includesNearmap) return `nearmap`

  return `nearmap`
}

const newImageSourceState = atom<string | null>({
  key: `newImageSourceState`,
  default: null,
})

const ConfirmImageSwitch: React.FC<{ preserveLayout: boolean }> = ({
  preserveLayout,
}) => {
  const setImageSource = useSetRecoilState(imageSourceState)
  const newImageSource = useRecoilValue(newImageSourceState)
  const setNewImageSource = useSetRecoilState(newImageSourceState)
  const resetPhases = useResetAllPhases()
  const { t } = useTranslation()
  const cancel = () => {
    setNewImageSource(null)
  }

  return (
    <FloatingPortal>
      {newImageSource && (
        <ConfirmModal
          Heading={() => (
            <h4>
              {t(
                preserveLayout ? `replaceImageKeepsLayout` : `designWillBeReset`,
              )}
            </h4>
          )}
          onClose={cancel}
          onSubmit={() => {
            setImageSource([newImageSource])
            setNewImageSource(null)
            // Recovery replaces only the background; existing panel placements stay put.
            if (!preserveLayout) resetPhases()
          }}
        />
      )}
    </FloatingPortal>
  )
}
